"use client";

import { useMemo, useState } from "react";
import { useFinance, useFinanceSync } from "@/lib/finance/finance-data-context";
import { usePersistedState } from "@/lib/finance/usePersistedState";
import { fmtDate, fmtDateTime, fmtUsd } from "@/lib/finance/format";
import { Card } from "@/components/finance/ui";
import type { Account } from "@/lib/finance/types";
import {
  GATHER_RULES,
  GATHER_LIMITATIONS,
  SOURCES,
  type SourceKey,
  sourceForAccount,
  submitAccountName,
  daysSinceUpdate,
  needsRefresh,
  backfillable,
  TXN_LOOKBACK_BUFFER_DAYS,
} from "@/lib/finance/gather";
import { assembleSubmission, balKey, parseEmpower, type EmpowerBatch } from "@/lib/finance/importParse";
import { findDuplicateGroups, type DuplicateGroup } from "@/lib/finance/aggregate";
import { addDays, todayISO } from "@/lib/finance/format";

/**
 * Gather & Import console — the in-app controller for Cowork. The app knows
 * each account's freshness (asOf), so it tells the agent exactly which accounts
 * need a fresh pull, from which source, and how.
 *
 * Empower is the aggregator: everything it covers is pulled in one session and
 * submitted through ONE combined box — `{balances, transactions}` — so the agent
 * dumps the whole pull at once instead of juggling a box per account. The few
 * direct-login / point accounts (Zillow, crypto, Lively, CB&T) keep their own
 * single-value boxes. Everything persists to localStorage until you import.
 */

interface Summary {
  transactionsAdded: number;
  transactionsUpdated: number;
  transactionsTotal: number;
  balanceMonths: string[];
  balancesAsOf: string;
  latestTxnDate: string | null;
  netWorth: number | null;
  transactionsUnchanged?: number;
  transactionsPending?: number;
  transactionsProtected?: number;
  transactionsDeleted?: number;
  duplicates?: { groups: DuplicateGroup[]; redundantRows: number; truncated?: number };
  dryRun?: boolean;
}

const EMPOWER_EXAMPLE = `{
  "balances": [
    {"date":"2026-07-15","institution":"Charles Schwab","owner":"Joint","type":"Checking","account":"Investor Checking","balance":24120.55},
    {"date":"2026-07-15","institution":"Vanguard","owner":"Jeff","type":"Retirement","account":"Google 401k - INPUT","balance":301000}
  ],
  "transactions": [
    {"id":"1234567890","date":"2026-07-14","account":"Investor Checking","description":"Costco","original":"COSTCO WHSE #0423","amount":-142.55,"category":"General Merchandise","tags":["Reimbursable"],"status":"posted"}
  ]
}`;

/** How far back the duplicates panel looks. Long enough to cover a missed
 * gather cycle, short enough that the list stays actionable. */
const DUPE_WINDOW_DAYS = 60;

const SOURCE_ORDER: SourceKey[] = ["empower", "zillow", "crypto", "lively", "cbt-heloc"];

interface PlanItem {
  source: SourceKey;
  institution: string;
  owner: string;
  type: string;
  account: string; // the balance-row identity to submit (INPUT for 401k)
  isLiability: boolean;
  asOf?: string;
  days: number | null;
  /** Whether this run should refresh the account — always true for Empower
   * (always pulled), else driven by the account's own staleness cadence. */
  needsRefresh: boolean;
  /** Has Empower daily history → backfill day-by-day (vs. a single current value). */
  backfill: boolean;
  /** First date to backfill from (asOf + 1 day), for backfillable accounts. */
  backfillFrom?: string;
}

/** ISO date `n` days after `iso` (local calendar), or undefined if unparseable. */
function addDaysISO(iso: string | undefined, n: number): string | undefined {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return undefined;
  return addDays(iso, n);
}

export function ImportConsole() {
  const { accounts, meta, txns } = useFinance();
  const { refresh } = useFinanceSync();
  // The one Empower box (all its balances + transactions) and per-account
  // single values for the manual/direct accounts — persisted so a half-finished
  // gather survives navigation/reload.
  const [empowerText, setEmpowerText] = usePersistedState<string>("fin.gather.empower", "");
  const [balInputs, setBalInputs] = usePersistedState<Record<string, string>>("fin.gather.balances", {});
  const [asOf, setAsOf] = useState<string>(todayISO);
  const [busy, setBusy] = useState<"dry" | "import" | null>(null);
  const [result, setResult] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Duplicate candidates, computed from the ledger the PWA has already synced
  // — no endpoint, no extra read, works offline. The window is deliberately
  // recent: older look-alikes are overwhelmingly real repeat charges, and
  // listing them forever trains you to ignore the panel.
  const dupeGroups = useMemo(
    () => (txns ? findDuplicateGroups(txns, addDays(todayISO(), -DUPE_WINDOW_DAYS)) : []),
    [txns],
  );

  // Build the gather plan: one item per submittable balance identity
  // (401k Roth+Traditional collapse to one INPUT), with freshness.
  const plan = useMemo<PlanItem[]>(() => {
    const now = new Date();
    const groups = new Map<string, Account[]>();
    for (const a of accounts) {
      if (!a.active) continue;
      const key = `${a.institution}|${a.owner}|${submitAccountName(a)}`;
      (groups.get(key) ?? groups.set(key, []).get(key)!).push(a);
    }
    return [...groups.values()].map((members) => {
      const rep = members[0];
      // Oldest observation in the group drives staleness.
      const asOfDate = members.map((m) => m.asOf).filter(Boolean).sort()[0];
      const withAsOf = { ...rep, asOf: asOfDate } as Account;
      const canBackfill = backfillable(rep);
      return {
        source: sourceForAccount(rep),
        institution: rep.institution,
        owner: rep.owner,
        type: rep.type,
        account: submitAccountName(rep),
        isLiability: rep.isLiability,
        asOf: asOfDate,
        days: daysSinceUpdate(withAsOf, now),
        needsRefresh: needsRefresh(withAsOf, now),
        backfill: canBackfill,
        backfillFrom: canBackfill ? addDaysISO(asOfDate, 1) : undefined,
      };
    });
  }, [accounts]);

  // Empower accounts are always refreshed; only the manual/direct accounts
  // carry a staleness nudge, so the header summary counts those separately.
  const empowerCount = plan.filter((p) => p.source === "empower").length;
  const manualNeedRefresh = plan.filter((p) => p.source !== "empower" && p.needsRefresh).length;

  // Transaction re-pull window: from (latest imported txn − overlap buffer) to
  // today. The overlap covers posting lag + re-categorization since the last run.
  const txnPullFrom = addDaysISO(meta.latestTxnDate ?? undefined, -TXN_LOOKBACK_BUFFER_DAYS);

  // Parse the Empower box once.
  const empower = useMemo(() => parseEmpower(empowerText), [empowerText]);

  const manualFilled = plan.filter(
    (p) => p.source !== "empower" && (balInputs[balKey(p)] ?? "").trim() !== "",
  ).length;

  const setOne = (key: string, value: string) => setBalInputs({ ...balInputs, [key]: value });
  const clearAll = () => {
    setEmpowerText("");
    setBalInputs({});
  };

  async function submit(dryRun: boolean) {
    setError(null);
    setResult(null);
    const { balances, transactions, overrideIds, splits, unsplits, bad } = assembleSubmission({ empower, manualItems: plan, balInputs, asOf });
    if (bad.length) {
      setError(`Fix before importing: ${bad.join(", ")}`);
      return;
    }
    setBusy(dryRun ? "dry" : "import");
    try {
      const res = await fetch("/api/finance/import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          transactions,
          balances,
          dryRun,
          ...(overrideIds.length ? { overrideIds } : {}),
          ...(splits.length ? { splits } : {}),
          ...(unsplits.length ? { unsplits } : {}),
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error ?? `HTTP ${res.status}`);
        return;
      }
      setResult(json as Summary);
      if (!dryRun) {
        clearAll(); // committed — start the next gather clean
        await refresh();
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(null);
    }
  }

  /** Delete ONE row. Deliberately not a bulk "clean all": which copy to keep
   * is a judgement the app can't make (see findDuplicateGroups), so each
   * removal is its own decision and its own audited write. */
  async function deleteTxn(sourceId: string) {
    setError(null);
    setDeleting(sourceId);
    try {
      const res = await fetch("/api/finance/import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deletes: [sourceId] }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error ?? `HTTP ${res.status}`);
        return;
      }
      await refresh(); // pull the shrunk ledger so the panel re-derives
    } catch (e) {
      setError(String(e));
    } finally {
      setDeleting(null);
    }
  }

  const importLabel = (() => {
    const nb = empower.balances.length + manualFilled;
    const nt = empower.txns.length;
    if (!nb && !nt) return "Import";
    const parts: string[] = [];
    if (nb) parts.push(`${nb} balance${nb === 1 ? "" : "s"}`);
    if (nt) parts.push(`${nt} txn${nt === 1 ? "" : "s"}`);
    return `Import ${parts.join(" + ")}`;
  })();

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Gather &amp; Import</h1>
        <p className="mt-0.5 text-xs text-ink-3">
          {meta.counts.mintTransactions.toLocaleString()} transactions · balances as of{" "}
          {fmtDateTime(meta.balancesAsOf)} · Empower ({empowerCount}) always refreshed
          {manualNeedRefresh > 0 && ` · ${manualNeedRefresh} manual account${manualNeedRefresh === 1 ? "" : "s"} need a refresh`}
          {" · "}Empower box: {empower.balances.length} balance{empower.balances.length === 1 ? "" : "s"},{" "}
          {empower.txns.length} txn{empower.txns.length === 1 ? "" : "s"}
        </p>
      </div>

      {/* Standing rules */}
      <Card title="Operating rules" subtitle="Always in force">
        <ol className="list-decimal space-y-1.5 pl-5 text-sm text-ink-2 marker:text-ink-3">
          {GATHER_RULES.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ol>
        <div className="mt-3 border-t border-edge pt-3">
          <p className="mb-1 text-xs font-medium text-ink-3">Known limitations</p>
          <ul className="list-disc space-y-1 pl-5 text-xs text-ink-3">
            {GATHER_LIMITATIONS.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </div>
      </Card>

      {/* As-of date for the manual single-value accounts below */}
      <Card
        title="Balance as-of date"
        subtitle="The day the manual single-value accounts (Zillow, crypto, Lively, CB&T) land on. Empower rows carry their own dates in the combined box."
      >
        <input
          type="date"
          value={asOf}
          onChange={(e) => setAsOf(e.target.value)}
          className="rounded-lg border border-edge bg-surface-2 px-3 py-1.5 text-sm text-ink focus:border-(--accent) focus:outline-none"
        />
      </Card>

      {SOURCE_ORDER.map((key) => {
        const items = plan.filter((p) => p.source === key);
        if (!items.length) return null;
        const src = SOURCES[key];
        const isEmpower = key === "empower";
        const need = items.filter((i) => i.needsRefresh).length;
        return (
          <Card
            key={key}
            title={src.label}
            subtitle={src.url}
            actions={
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  isEmpower ? "bg-accent/15 text-accent-ink" : need ? "bg-warn/15 text-warn" : "text-ink-3"
                }`}
              >
                {isEmpower ? "always refreshed" : need ? `${need} need update` : "all fresh"}
                {src.manual && " · needs Jeff"}
              </span>
            }
          >
            <ol className="mb-3 list-decimal space-y-1 pl-5 text-xs text-ink-2 marker:text-ink-3">
              {src.steps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>

            {isEmpower ? (
              <>
                {/* Read-only account guidance: what to pull and from when */}
                <ul className="mb-3 divide-y divide-edge rounded-lg border border-edge">
                  {items.map((it) => (
                    <li key={balKey(it)} className="flex items-center justify-between gap-3 px-3 py-1.5">
                      <div className="min-w-0">
                        <span className="text-xs font-medium">{it.account}</span>
                        <span className="text-[11px] text-ink-3">
                          {" "}
                          · {it.owner} · {it.type}
                          {it.isLiability && " · negative"}
                        </span>
                      </div>
                      <div className="flex shrink-0 items-center gap-2 text-[11px] text-ink-3">
                        <span>
                          {it.backfill
                            ? it.backfillFrom
                              ? `${it.asOf ? `last ${fmtDate(it.asOf)} · ` : ""}from ${fmtDate(it.backfillFrom)}`
                              : "full history"
                            : "current value"}
                        </span>
                        {/* Always pulled — no staleness gate for Empower. */}
                        <span className="rounded-full bg-accent/15 px-2 py-0.5 font-medium text-accent-ink">refresh</span>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-medium text-ink-2">
                    All Empower balances + transactions (one JSON object)
                  </label>
                  <EmpowerBadge batch={empower} />
                </div>
                <p className="mb-2 text-[11px] text-ink-3">
                  {`balances: {date, institution, owner, type, account, balance} — liabilities negative, 401k total → "… - INPUT". `}
                  {`transactions pull window ${txnPullFrom ? `${fmtDate(txnPullFrom)} → today` : "all available"} (last ${
                    meta.latestTxnDate ?? "—"
                  } − ${TXN_LOOKBACK_BUFFER_DAYS}d); each needs account + status "pending"/"posted" (else the import is rejected) + id + tags. `}
                  <span className="font-medium">Import only NEW rows</span>
                  {`: dry-run first — normalize descriptions/categories/tags per the drift hints, drop every id the summary lists in updatedIds (already imported; app-side curation owns those), then import.`}
                </p>
                <p className="mb-2 text-[11px] text-ink-3">
                  Optional, same object · <span className="font-medium">splits</span>:{" "}
                  <code className="text-ink-2">{`[{sourceId, parts:[{amount, category, description?, date?, tags?}]}]`}</code>{" "}
                  — parts must sum to the original (Empower loses split detail on export). ·{" "}
                  <span className="font-medium">unsplits</span>: <code className="text-ink-2">[sourceId]</code> to reverse a split. ·{" "}
                  <span className="font-medium">deletes</span>: <code className="text-ink-2">[sourceId]</code> to remove a row outright. ·{" "}
                  <span className="font-medium">overrideIds</span>: <code className="text-ink-2">[sourceId]</code> the ONLY way to change a row the ledger already holds.
                </p>
                <textarea
                  value={empowerText}
                  onChange={(e) => setEmpowerText(e.target.value)}
                  placeholder={EMPOWER_EXAMPLE}
                  spellCheck={false}
                  rows={12}
                  className="w-full rounded-lg border border-edge bg-surface-2 p-3 font-mono text-xs text-ink placeholder:text-ink-3/70 focus:border-(--accent) focus:outline-none"
                />
              </>
            ) : (
              <ul className="divide-y divide-edge">
                {items.map((it) => {
                  const k = balKey(it);
                  return (
                    <li key={k} className="flex items-center justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{it.account}</div>
                        <div className="truncate text-xs text-ink-3">
                          {it.institution} · {it.owner} · {it.type}
                          {it.isLiability && " · enter negative"}
                        </div>
                        <div className="mt-0.5 text-[11px] text-ink-3">
                          {it.asOf ? `updated ${fmtDate(it.asOf)}` : "never"}
                          {it.days != null && ` · ${it.days}d`} · one current value
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <FreshnessBadge stale={it.needsRefresh} filled={(balInputs[k] ?? "").trim() !== ""} />
                        <input
                          inputMode="decimal"
                          value={balInputs[k] ?? ""}
                          onChange={(e) => setOne(k, e.target.value)}
                          placeholder="balance"
                          spellCheck={false}
                          className="w-32 rounded-lg border border-edge bg-surface-2 px-2.5 py-1.5 text-right font-mono text-sm tabular-nums text-ink placeholder:text-ink-3/70 focus:border-(--accent) focus:outline-none"
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        );
      })}

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => submit(true)}
          disabled={busy !== null}
          className="rounded-full border border-edge px-4 py-1.5 text-sm font-medium text-ink-2 hover:bg-surface-3 disabled:opacity-60"
        >
          {busy === "dry" ? "Previewing…" : "Dry run (preview)"}
        </button>
        <button
          onClick={() => submit(false)}
          disabled={busy !== null}
          className="rounded-full bg-accent px-4 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
        >
          {busy === "import" ? "Importing…" : importLabel}
        </button>
        {(empowerText.trim() || manualFilled > 0) && (
          <button
            onClick={clearAll}
            disabled={busy !== null}
            className="rounded-full px-3 py-1.5 text-xs font-medium text-ink-3 hover:text-ink-2 disabled:opacity-60"
          >
            Clear all
          </button>
        )}
      </div>

      {error && (
        <Card>
          <p className="text-sm text-neg">{error}</p>
        </Card>
      )}

      {dupeGroups.length > 0 && (
        <Card title={`Possible duplicates (${dupeGroups.reduce((n, g) => n + g.rows.length - 1, 0)})`}>
          <p className="mb-3 text-xs text-ink-3">
            Same day, same account, same descriptor, same amount — in the last {DUPE_WINDOW_DAYS} days.
            Empower re-issues transaction ids, so a re-pull can land a row the ledger already has under a
            different id and the importer can&apos;t tell. <strong>These are candidates, not verdicts</strong> —
            repeat charges look identical here (eleven $3 transit fares in a day are eleven real rides).
            Keep one of each group and delete the rest, or leave them alone.
          </p>
          <div className="space-y-3">
            {dupeGroups.map((g) => (
              <div key={`${g.date}|${g.accountId}|${g.descriptor}|${g.amount}`} className="rounded-lg border border-line p-2">
                <div className="mb-1 flex items-baseline justify-between gap-3 text-xs text-ink-3">
                  <span className="truncate">
                    {fmtDate(g.date)} · {accounts.find((a) => a.id === g.accountId)?.name ?? g.accountId}
                  </span>
                  <span className="shrink-0 tabular-nums">{fmtUsd(g.amount)} × {g.rows.length}</span>
                </div>
                <ul className="space-y-1">
                  {g.rows.map((r) => (
                    <li key={r.sourceId} className="flex items-center justify-between gap-3 text-sm">
                      <span className="min-w-0 flex-1 truncate">
                        {r.description}
                        <span className="ml-2 font-mono text-[11px] text-ink-3">{r.sourceId}</span>
                      </span>
                      <button
                        type="button"
                        disabled={deleting !== null}
                        onClick={() => r.sourceId && deleteTxn(r.sourceId)}
                        className="shrink-0 rounded-md px-2 py-0.5 text-xs font-medium text-neg hover:bg-neg/10 disabled:opacity-40"
                      >
                        {deleting === r.sourceId ? "Deleting…" : "Delete"}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Card>
      )}

      {result && (
        <Card title={result.dryRun ? "Preview (nothing written)" : "Imported ✓"}>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
            <Stat label="Transactions added" value={result.transactionsAdded.toLocaleString()} />
            <Stat label="Overridden" value={(result.transactionsUpdated ?? 0).toLocaleString()} />
            <Stat label="Unchanged (re-sent)" value={(result.transactionsUnchanged ?? 0).toLocaleString()} />
            <Stat label="Refused (differed)" value={(result.transactionsProtected ?? 0).toLocaleString()} />
            <Stat label="Pending (dropped)" value={(result.transactionsPending ?? 0).toLocaleString()} />
            <Stat label="Total transactions" value={result.transactionsTotal.toLocaleString()} />
            <Stat label="Latest txn" value={result.latestTxnDate ?? "—"} />
            <Stat label="Balance months" value={result.balanceMonths.join(", ") || "—"} />
            <Stat label="Net worth" value={result.netWorth != null ? fmtUsd(result.netWorth) : "—"} />
            {result.transactionsDeleted ? <Stat label="Deleted" value={result.transactionsDeleted.toLocaleString()} /> : null}
          </dl>
          {result.duplicates && (
            <p className="mt-3 rounded-lg bg-warn/10 p-2 text-xs text-warn">
              ⚠ This submission landed on {result.duplicates.redundantRows} row
              {result.duplicates.redundantRows === 1 ? "" : "s"} that already look identical
              ({result.duplicates.groups.length} group{result.duplicates.groups.length === 1 ? "" : "s"}
              {result.duplicates.truncated ? ` of ${result.duplicates.truncated}` : ""}).
              {result.dryRun
                ? " Drop them from the submission before importing for real."
                : " Review them in the duplicates panel above."}
            </p>
          )}
        </Card>
      )}
    </div>
  );
}

function EmpowerBadge({ batch }: { batch: EmpowerBatch }) {
  if (batch.error)
    return (
      <span className="max-w-[60%] truncate rounded-full bg-neg/15 px-2 py-0.5 text-[11px] font-medium text-neg">
        {batch.error}
      </span>
    );
  const n = batch.balances.length + batch.txns.length;
  if (n === 0)
    return <span className="rounded-full px-2 py-0.5 text-[11px] font-medium text-ink-3">empty</span>;
  return (
    <span className="rounded-full bg-pos/15 px-2 py-0.5 text-[11px] font-medium text-pos">
      {batch.balances.length} bal · {batch.txns.length} tx ✓
    </span>
  );
}

function FreshnessBadge({ stale, filled }: { stale: boolean; filled: boolean }) {
  if (filled)
    return (
      <span className="inline-block rounded-full bg-pos/15 px-2 py-0.5 text-[11px] font-medium text-pos">
        Entered ✓
      </span>
    );
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${
        stale ? "bg-warn/15 text-warn" : "bg-pos/15 text-pos"
      }`}
    >
      {stale ? "Needs update" : "Fresh"}
    </span>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-ink-3">{label}</dt>
      <dd className="font-medium tabular-nums text-ink">{value}</dd>
    </div>
  );
}
